/**
 * Spec 10.2.1.2 — approval magic-link landing. Exchanges the challenge-less code
 * the Worker issued (no client PKCE on this device) for a session, then deep-links
 * into the approvals inbox at the exact authorisation. Reuse/expiry is handled by
 * the Worker's error page before this ever renders.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { handleApprovalCallback } from "../auth";
import { useV3Theme } from "../../v3/hooks/use-v3-theme";
import { AuthShell } from "./AuthShell";
import { AuthSpinner } from "./AuthSpinner";

export function ApprovalCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get("code");
  const authId = searchParams.get("auth");
  const [error, setError] = useState<string | null>(code ? null : "This approval link is missing its code.");
  const handled = useRef(false);
  // №14.1.1-C — folded off the sixth orphan system onto `app-chrome`.
  useV3Theme();

  useEffect(() => {
    if (handled.current || !code) return;
    handled.current = true;
    handleApprovalCallback(code).then((ok) => {
      if (ok) navigate(`/app${authId ? `?auth=${encodeURIComponent(authId)}` : ""}`, { replace: true });
      else setError("We couldn’t sign you in from this link. Ask a signatory for a fresh one.");
    });
  }, [code, authId, navigate]);

  if (error) {
    return (
      <AuthShell>
        <div className="authpane__card">
          <h1>This approval link can’t be used</h1>
          <p className="authpane__sub">{error}</p>
          <a className="btn btn--primary" href="/app">Open the approvals inbox</a>
        </div>
      </AuthShell>
    );
  }
  return <AuthSpinner label="Opening your approval…" />;
}
